import { useMemo } from 'react'
import { useSearch } from '@tanstack/react-router'
import { useTranslation } from 'react-i18next'
import { useClub } from '../../club/ClubProvider.tsx'
import { useAreas, type AreaRow } from '../../data/geo.ts'
import { EXCLUSION_REASON_KEY, courseGreens, num, sectorText, sheetLines, usePinExclusions, usePinPlacements } from '../../data/pinsUi.ts'
import { areaShort, longDay } from '../../data/planUi.ts'
import { fmtNum } from '../../lib/format.ts'
import { localDate } from '../../lib/time.ts'
import { BackLink } from '../../ui/components.tsx'

const ISO = /^\d{4}-\d{2}-\d{2}$/

/** The pin sheet to print at dawn (M16-C): one line per green of the course with the day's sector, the offsets in metres, the note, and the exclusions
 *  in force. Built from the device's own copy, so the shed prints it with no network (a workstation caches the three tables); the daily sheet's print
 *  pattern (A4, 8-mm margins, black on white: `.plan-sheet` in index.css). A green with no placement keeps its line with a dash: the sheet is also
 *  filled by hand. The reviewed sheet leaves the building only as a course notice of kind "pins" (M16-B1). */
export function PinsPrintPage() {
  const { t, i18n } = useTranslation()
  const { org, site } = useClub()
  const lang = i18n.language
  const search = useSearch({ strict: false }) as { d?: string }
  const date = search.d && ISO.test(search.d) ? search.d : localDate(new Date(), site.timezone)
  const areas = useAreas(); const placements = usePinPlacements(); const exclusions = usePinExclusions()
  const areaById = useMemo(() => new Map((areas ?? []).map((a) => [a.id, a as AreaRow])), [areas])
  const greens = useMemo(() => courseGreens((areas ?? []) as AreaRow[], lang), [areas, lang])
  if (!areas || !placements || !exclusions) return <p>{t('common.loading')}</p>
  const lines = sheetLines(greens, placements, exclusions, date)
  const placed = lines.filter((l) => l.placement).length
  const m = (v: number | string | null) => (num(v) === null ? '' : fmtNum(num(v)!, lang, 1))
  return (
    <section className="plan-sheet flex flex-col gap-4 print:gap-2 print:text-[10pt] print:leading-[1.2]" data-testid="pins-sheet" data-lines={lines.length} data-placed={placed}>
      <div className="flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between print:hidden">
        {/* the route is declared in router.tsx by Z's close (docs/m16-requests/c.md): the cast goes the day it is */}
        <BackLink to="/plan/pins" search={{ d: date }} testId="pins-sheet-back" className="sm:w-auto">{t('pins.sheet.back')}</BackLink>
        <button className="btn btn-primary" onClick={() => window.print()} data-testid="pins-sheet-print">{t('plan.printNow')}</button>
      </div>
      <header className="flex flex-col gap-0.5 border-b-2 border-slate-900 pb-2 dark:border-slate-100 print:border-black print:pb-1">
        <p className="text-sm print:text-[10pt]">{org.org_name} · {site.name}</p>
        <h1 className="text-2xl font-bold first-letter:uppercase print:text-[14pt]">{t('pins.sheet.title')} — {longDay(date, lang)}</h1>
      </header>
      {placed === 0 && <p className="meta print:text-black" data-testid="pins-sheet-none">{t('pins.sheet.none')}</p>}
      {/* M16a review: a percentage-width table collapses at phone width (the two offset columns got 41 px each); under sm the lines are cards, the table is for sm and the printer */}
      <ul className="flex flex-col gap-2 sm:hidden print:hidden" data-testid="pins-sheet-cards">
        {lines.map((l) => (
          <li key={l.area.id} className="card flex flex-col gap-1" data-testid="pins-sheet-card" data-area={l.area.id} data-sector={l.placement?.sector_code ?? ''}>
            <p className="font-semibold">{areaShort(l.area, areaById, lang, t)} · {l.placement ? sectorText(l.placement.sector_code, t) : '—'}</p>
            {l.placement && (num(l.placement.offset_front_m) !== null || num(l.placement.offset_side_m) !== null) && (
              <p className="meta">{t('pins.sheet.front')} : {m(l.placement.offset_front_m) || '—'} · {t('pins.sheet.side')} : {m(l.placement.offset_side_m) || '—'}</p>
            )}
            {l.placement?.note && <p className="meta">{l.placement.note}</p>}
            {l.exclusions.length > 0 && <p className="meta">{t('pins.sheet.exclusions')} : {l.exclusions.map((x) => `${x.label} (${t(EXCLUSION_REASON_KEY[x.reason])})`).join(', ')}</p>}
          </li>
        ))}
      </ul>
      <table className="hidden w-full border-collapse text-left text-base sm:table print:table print:text-[10pt]" data-testid="pins-sheet-table">
        <thead>
          <tr className="border-b-2 border-slate-900 dark:border-slate-100 print:border-black">
            <th className="w-[30%] p-2 print:py-0.5">{t('pins.sheet.green')}</th>
            <th className="w-[22%] p-2 print:py-0.5">{t('pins.sheet.sector')}</th>
            <th className="w-[12%] p-2 text-right print:py-0.5">{t('pins.sheet.front')}</th>
            <th className="w-[12%] p-2 text-right print:py-0.5">{t('pins.sheet.side')}</th>
            <th className="p-2 print:py-0.5">{t('pins.sheet.note')}</th>
          </tr>
        </thead>
        <tbody>
          {lines.map((l) => (
            <tr key={l.area.id} className="break-inside-avoid border-b border-slate-300 align-top dark:border-slate-600 print:border-black" data-testid="pins-sheet-row" data-area={l.area.id} data-sector={l.placement?.sector_code ?? ''}>
              <td className="p-2 print:py-0.5">
                <p className="font-semibold">{areaShort(l.area, areaById, lang, t)}</p>
                {l.exclusions.length > 0 && <p className="meta print:text-[9pt] print:text-black">{t('pins.sheet.exclusions')} : {l.exclusions.map((x) => `${x.label} (${t(EXCLUSION_REASON_KEY[x.reason])})`).join(', ')}</p>}
              </td>
              <td className="p-2 font-semibold print:py-0.5">{l.placement ? sectorText(l.placement.sector_code, t) : '—'}</td>
              <td className="p-2 text-right tabular-nums print:py-0.5">{l.placement ? m(l.placement.offset_front_m) : ''}</td>
              <td className="p-2 text-right tabular-nums print:py-0.5">{l.placement ? m(l.placement.offset_side_m) : ''}</td>
              <td className="p-2 print:py-0.5">{l.placement?.note ?? ''}</td>
            </tr>
          ))}
        </tbody>
      </table>
      <p className="meta print:text-[9pt] print:text-black">{t('pins.sheet.footer')}</p>
    </section>
  )
}
